import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Textarea,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useServiceOrder,
  useAddServiceOrderComment,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  SERVICE_ORDER_PRIORITY_LABEL,
  SERVICE_ORDER_PRIORITY_VARIANT,
  SERVICE_ORDER_TYPE_LABEL,
} from './useServiceOrders';
import { ServiceOrderPicker } from './ServiceOrderPicker';
import { formatDateTime } from '../../shared/utils/format';

interface ServiceOrderCommentsPageProps {
  /** Id da OS. Se ausente, le `?os=` da URL; sem isso, mostra o seletor. */
  orderId?: string;
}

/**
 * Historico e comentarios de uma Ordem de Servico.
 *
 * Consome:
 *   GET  /manufacturing/service-orders/:id  (agregado — lista de comments)
 *   POST /manufacturing/service-orders/:id/comments
 *        body: { authorName, body }
 *        resp: { commentId, createdAt }
 */
export function ServiceOrderCommentsPage({
  orderId,
}: ServiceOrderCommentsPageProps): JSX.Element {
  const { addToast } = useToast();

  const urlOrderId = useMemo(
    () => new URLSearchParams(window.location.search).get('os') ?? undefined,
    [],
  );
  const [pickedId, setPickedId] = useState<string | null>(null);
  const activeId = orderId ?? pickedId ?? urlOrderId;

  const { data: detail, isLoading, error, refetch } = useServiceOrder(activeId);

  const [form, setForm] = useState({ authorName: '', body: '' });
  const addComment = useAddServiceOrderComment();

  /* Linha do tempo cronológica: mais antigo primeiro, como o protótipo. */
  const comments = useMemo(
    () => [...(detail?.comments ?? [])].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [detail],
  );

  if (!activeId) {
    return (
      <div className="so-page">
        <ServiceOrderPicker onSelect={setPickedId} label="Histórico e Comentários" />
      </div>
    );
  }

  const handleSubmit = async (): Promise<void> => {
    if (!form.authorName.trim() || !form.body.trim()) {
      addToast('Informe o autor e o texto do comentário', 'error');
      return;
    }
    try {
      await addComment.mutateAsync({
        id: activeId,
        authorName: form.authorName.trim(),
        body: form.body.trim(),
      });
      addToast('Comentário registrado no histórico', 'success');
      setForm({ authorName: '', body: '' });
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao registrar comentário', 'error');
    }
  };

  return (
    <div className="so-page">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <div className="flex items-center gap-sm mb-xs flex-wrap">
            <span
              className="font-label-md text-label-md px-2 py-1 rounded"
              style={{ background: '#eceef0', color: '#43474e' }}
            >
              {detail?.number ?? 'Carregando...'}
            </span>
            {detail && (
              <Badge variant={SERVICE_ORDER_STATUS_VARIANT[detail.status]} size="sm">
                {SERVICE_ORDER_STATUS_LABEL[detail.status]}
              </Badge>
            )}
          </div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Histórico e Comentários</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Linha do tempo das interações da OS. Comentários entram no histórico da ordem.
          </p>
        </div>
      </div>

      {/* ── Loading / Error ── */}
      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="state-error" role="alert">
            <p>Erro ao carregar a ordem de serviço</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {detail && !isLoading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">
          {/* ── Timeline ── */}
          <div className="lg:col-span-2">
            <Card padding="0">
              <CardHeader
                className="px-md pt-md"
                action={
                  <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                    {comments.length} {comments.length === 1 ? 'comentário' : 'comentários'}
                  </span>
                }
              >
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Linha do tempo da OS</h3>
              </CardHeader>
              <div className="so-comments">
                {comments.length === 0 && (
                  <div className="state-empty">
                    Nenhum comentário registrado. Use o formulário “Adicionar Comentário” para iniciar o histórico.
                  </div>
                )}
                {comments.map((c) => (
                  <div key={c.id} className="so-comment">
                    <div className="so-comment__dot">💬</div>
                    <div className="so-comment__body">
                      <div className="so-comment__head">
                        <strong>{c.authorName}</strong>
                        <span className="so-comment__date">{formatDateTime(c.createdAt)}</span>
                      </div>
                      <p className="m-0">{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* ── Ação + resumo ── */}
          <div className="flex flex-col gap-md">
            <Card padding="1.25rem">
              <h4 className="font-headline-sm text-headline-sm text-on-surface mb-sm flex items-center gap-xs">
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 20 }}>post_add</span>
                Adicionar Comentário
              </h4>
              <div className="so-form">
                <Input
                  label="Autor *"
                  value={form.authorName}
                  onChange={(e) => setForm((p) => ({ ...p, authorName: e.target.value }))}
                  placeholder="Nome de quem está comentando"
                />
                <Textarea
                  label="Comentário *"
                  value={form.body}
                  onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                  placeholder="Digite a nota ou atualização aqui..."
                  rows={4}
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmit} loading={addComment.isPending}>Salvar Comentário</Button>
                </div>
              </div>
            </Card>

            <Card padding="1.25rem">
              <h4 className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider mb-sm">
                Resumo da OS
              </h4>
              <dl className="so-summary">
                <div><dt>Número</dt><dd>{detail.number}</dd></div>
                <div><dt>Status</dt><dd>{SERVICE_ORDER_STATUS_LABEL[detail.status]}</dd></div>
                <div><dt>Prioridade</dt><dd>{SERVICE_ORDER_PRIORITY_LABEL[detail.priority]}</dd></div>
                <div><dt>Tipo</dt><dd>{SERVICE_ORDER_TYPE_LABEL[detail.type]}</dd></div>
                <div><dt>Cliente</dt><dd>{detail.customerRef ?? '—'}</dd></div>
                <div><dt>Equipamento</dt><dd>{detail.equipmentRef ?? '—'}</dd></div>
              </dl>
              {detail.priority === 'HIGH' || detail.priority === 'URGENT' ? (
                <Badge variant={SERVICE_ORDER_PRIORITY_VARIANT[detail.priority]} size="sm" dot>
                  Prioridade {SERVICE_ORDER_PRIORITY_LABEL[detail.priority]}
                </Badge>
              ) : null}
            </Card>
          </div>
        </div>
      )}

      <style>{`
        .so-page { padding: 1.5rem; }
        .so-comments { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 1rem; }
        .so-comment { display: flex; gap: 0.75rem; }
        .so-comment__dot {
          width: 32px; height: 32px; border-radius: 50%; background: #f2f4f6;
          border: 2px solid #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .so-comment__body { flex: 1; background: #fafbfc; border: 1px solid #eceef0; border-radius: 8px; padding: 0.625rem 0.875rem; }
        .so-comment__head { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; margin-bottom: 0.25rem; }
        .so-comment__date { font-size: 0.75rem; color: #74777f; }
        .so-form { display: flex; flex-direction: column; gap: 1rem; }
        .so-summary { margin: 0; }
        .so-summary > div { display: flex; gap: 0.5rem; border-bottom: 1px solid #eceef0; padding: 0.375rem 0; }
        .so-summary dt { color: #74777f; font-size: 0.8125rem; min-width: 100px; }
        .so-summary dd { margin: 0; color: #191c1e; font-size: 0.875rem; font-weight: 500; text-align: right; flex: 1; }
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1.5rem 0.5rem; color: #74777f; }
      `}</style>
    </div>
  );
}